import {observer} from "mobx-react";
import React from "react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import type {IMenuController} from "views/components/menu/menu.controller";
import styled from "@emotion/styled";
import {Box, Button, useMediaQuery} from "@mui/material";
import {getOpenState} from "data/utils/helper";
import {MENU_LINKS, MOBILE_MENU_LINKS, NOT_AUTH_MOBILE_MENU_LINKS, SITE_URL} from "data/constants";
import {NavLink} from "react-router-dom";
import {ReactComponent as CloseIcon} from "assets/img/icon/close-white.svg";
import {media} from "assets/css/media";
import {ReactComponent as PinpointLogo} from "assets/img/gamezone/pinpoint_logo.svg";

const MobileMenuWrapper = styled(Box)`
	position: fixed;
	width: 100%;
	max-width: 320px;
	overflow: auto;
	top: 0;
	bottom: 0;
	left: 0;
	background: var(--primaryColor);
	z-index: 999;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	transition: all ease 0.5s;
	background-size: cover;

	&.open {
		transform: translateX(0);
	}

	&.closed {
		transform: translateX(-100%);
	}
`;

const MenuContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	padding: 10px 0;

	a {
		font-family: var(--fontFamilySecond);
		font-style: normal;
		font-weight: 400;
		font-size: 16px;
		line-height: 118%;

		display: flex;
		align-items: center;
		letter-spacing: 0.06em;
		text-transform: capitalize;
		color: #ffffff;

		opacity: 0.9;
		transition: all ease 0.4s;
		padding: 5px 20px;
		border-bottom: 1px solid #011536;
		height: 40px;

		&.active {
			opacity: 1;
			font-weight: 450;
			color: var(--thirdColor);
		}
	}
`;
const MenuHeader = styled(Box)`
	min-height: 54px;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 5px 0 5px;
	box-sizing: border-box;
	position: relative;

	button {
		width: 40px;
		height: 40px;
		min-width: 40px;

		svg {
			width: 20px;
			height: 20px;
		}
	}
`;
const MenuOverlay = styled(Box)<{isVisible: boolean}>`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.69);
	z-index: ${({isVisible}) => (isVisible ? 899 : -1)};
`;
const CloseButton = styled(Button)`
	position: absolute;
	left: 5px;
	top: 50%;
	transform: translateY(-50%);
	width: auto;
	height: auto;
	min-width: unset;
`;
const ProjectName = styled(Box)`
	font-family: var(--fontFamilySecond);
	font-style: normal;
	font-weight: 450;
	font-size: 16px;
	line-height: 118%;

	display: flex;
	align-items: center;
	letter-spacing: 0.06em;
	text-transform: capitalize;
	color: #ffffff;

	opacity: 0.9;
	transition: all ease 0.4s;
	padding: 5px 20px;
	border-bottom: 1px solid var(--secondaryColor);
	background: var(--thirdColor);
	height: 40px;

	a,
	span {
		height: auto;
		padding: 5px 0;
		font-weight: 400;
	}
`;

export const MobileMenu: React.FC = observer(() => {
	const {i18n, isOpenMenu, closeMenu, isAuthorized} = useViewController<IMenuController>(
		Bindings.MenuController
	);
	const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);

	const menuLinks = isAuthorized
		? isMobile
			? MOBILE_MENU_LINKS
			: MENU_LINKS
		: NOT_AUTH_MOBILE_MENU_LINKS;

	return isMobile ? (
		<MenuOverlay isVisible={isOpenMenu} onClick={closeMenu}>
			<MobileMenuWrapper className={getOpenState(isOpenMenu)}>
				<MenuContainer>
					<MenuHeader>
						<CloseButton onClick={closeMenu}>
							<CloseIcon />
						</CloseButton>

						<PinpointLogo width="112px" />
					</MenuHeader>
					<ProjectName>
						<span>{i18n.t("navigation.pinpoint_copy", "Ryder Cup Pinpoint")}</span>
					</ProjectName>
					{menuLinks.map((el, index) => (
						<NavLink key={index} to={el.url} onClick={closeMenu}>
							{i18n.t(el.name)}
						</NavLink>
					))}
					<ProjectName>
						<a href={`${SITE_URL}fantasy`}>
							{i18n.t("common.project.fantasy", "Fantasy")}
						</a>
					</ProjectName>
					<ProjectName>
						<a href={SITE_URL}>{i18n.t("common.project.gamezone", "Game Zone")}</a>
					</ProjectName>
				</MenuContainer>
			</MobileMenuWrapper>
		</MenuOverlay>
	) : null;
});
